import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { StockMovementsService, StockMovement } from '../../services/stock-movements.service';
import { StockMovementDialogComponent, StockMovementDialogData } from './stock-movement-dialog.component';

@Component({
  selector: 'app-stock-movements',
  templateUrl: './stock-movements.component.html',
  styleUrls: ['./stock-movements.component.css']
})
export class StockMovementsComponent implements OnInit {
  displayedColumns: string[] = ['id', 'productCode', 'productName', 'movementType', 'quantity', 'unitPrice', 'totalValue', 'referenceNumber', 'movementDate', 'location', 'performedBy', 'actions'];
  dataSource = new MatTableDataSource<StockMovement>();
  isLoading = false;
  selectedDate: Date = new Date();
  totalMovements: number = 0;
  totalIn: number = 0;
  totalOut: number = 0;
  totalValue: number = 0;

  constructor(
    private stockMovementsService: StockMovementsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadStockMovements();
  }

  loadStockMovements(): void {
    this.isLoading = true;
    this.stockMovementsService.getStockMovements().subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource.data = response.data;
          this.calculateTotals();
        } else {
          this.snackBar.open(response.message || 'Failed to load stock movements', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading stock movements:', error);
        this.snackBar.open('Error loading stock movements', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  calculateTotals(): void {
    this.totalMovements = this.dataSource.data.length;
    this.totalIn = this.dataSource.data.filter(item => item.movementType === 'IN').reduce((sum, item) => sum + item.quantity, 0);
    this.totalOut = this.dataSource.data.filter(item => item.movementType === 'OUT').reduce((sum, item) => sum + item.quantity, 0);
    this.totalValue = this.dataSource.data.reduce((sum, item) => sum + item.totalValue, 0);
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  dateChange(event: MatDatepickerInputEvent<Date>): void {
    this.selectedDate = event.value || new Date();
    this.loadStockMovements(); // Reload with new date filter
  }

  addStockMovement(): void {
    const dialogData: StockMovementDialogData = {
      title: 'Add New Stock Movement'
    };

    const dialogRef = this.dialog.open(StockMovementDialogComponent, {
      width: '700px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadStockMovements(); // Refresh the list
      }
    });
  }

  editMovement(movement: StockMovement): void {
    const dialogData: StockMovementDialogData = {
      title: 'Edit Stock Movement',
      stockMovement: movement
    };

    const dialogRef = this.dialog.open(StockMovementDialogComponent, {
      width: '700px',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadStockMovements(); // Refresh the list
      }
    });
  }

  viewMovementDetails(movement: StockMovement): void {
    this.snackBar.open(`Viewing details for ${movement.productName}`, 'Close', { duration: 3000 });
    // TODO: Implement movement details dialog
  }

  exportToExcel(): void {
    this.snackBar.open('Exporting to Excel...', 'Close', { duration: 3000 });
    this.stockMovementsService.exportToExcel().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock-movements-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Excel export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
        this.snackBar.open('Error exporting to Excel', 'Close', { duration: 3000 });
      }
    });
  }

  exportToPDF(): void {
    this.snackBar.open('Exporting to PDF...', 'Close', { duration: 3000 });
    this.stockMovementsService.exportToPDF().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock-movements-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.snackBar.open('PDF export completed', 'Close', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting to PDF:', error);
        this.snackBar.open('Error exporting to PDF', 'Close', { duration: 3000 });
      }
    });
  }
}
